import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SponsorsBanner from "../components/SponsorsBanner";
import { Spot } from "../components/Spot";
import { ArrowRight, MapPin, Coffee, Calendar, Phone, Globe, Heart, ChevronRight, Check, Star } from "lucide-react";

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setVisible(true));
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 1s ease ${delay}s, transform 1s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

const HeroSpot = ({ lang }) => (
  <div data-testid="hero-spot" className="lg:col-span-5 lg:order-2 relative flex justify-start lg:justify-center animate-fade-in delay-2">
    <div className="relative">
      <div className="spot-halo absolute -inset-8 lg:-inset-16" />
      <Spot eager sizes="(min-width: 1024px) 480px, 200px" className="relative h-[26vh] max-h-[240px] sm:h-[30vh] sm:max-h-[300px] lg:h-[min(64vh,720px)] xl:h-[min(76vh,720px)] lg:max-h-none w-auto" />
      <div data-testid="hero-spot-bubble" className="spot-bubble absolute left-full ml-4 top-[22%] w-[150px] sm:w-[210px] lg:left-[58%] lg:ml-0 lg:-top-8 xl:left-[66%] xl:-top-6 lg:w-[190px] xl:w-[230px] px-4 py-3 animate-fade-up delay-4">
        <div className="font-serif text-lg sm:text-xl leading-tight font-semibold">
          {lang === "en" ? "Hi, I'm Spot!" : "Salut, moi c'est Spot !"}
        </div>
        <div className="text-[11px] sm:text-xs leading-snug mt-1 text-[#5D4037]">
          {lang === "en" ? "Your guide to local cafés & bakeries." : "Votre guide des cafés et boulangeries d'ici."}
        </div>
      </div>
    </div>
  </div>
);

export default function Landing() {
  const { t, lang } = useI18n();
  const [cms, setCms] = useState({});
  const [cafes, setCafes] = useState([]);
  useEffect(() => { api.get("/content").then((r) => setCms(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/cafes").then((r) => setCafes(r.data)).catch(() => {}); }, []);
  const features = t("feature");
  const heroImg = cms.hero_image_url || "https://static.prod-images.emergentagent.com/jobs/4533eb4f-727d-4fa4-a61b-ad5c3b4e489f/images/9dc0043505d8abd54f72b9eb33755bd258bfe0608004aa726c4554e624455aa9.png";
  const heroTitle = (lang === "en" ? cms.hero_title_en : cms.hero_title_fr) || t("hero.title1");
  const heroAccent = (lang === "en" ? cms.hero_title_accent_en : cms.hero_title_accent_fr) || t("hero.title2");
  const heroSub = (lang === "en" ? cms.hero_sub_en : cms.hero_sub_fr) || t("hero.sub");
  const ctaTitle = (lang === "en" ? cms.cta_title_en : cms.cta_title_fr) || t("sections.ctaTitle");
  const ctaSub = (lang === "en" ? cms.cta_sub_en : cms.cta_sub_fr) || t("sections.ctaSub");
  const announce = (lang === "en" ? cms.banner_announce_en : cms.banner_announce_fr) || "";

  const featureIcons = [MapPin, Coffee, Calendar, Phone, Globe, Heart];

  const galleryImages = [
    "https://images.pexels.com/photos/11439366/pexels-photo-11439366.jpeg",
    "https://images.pexels.com/photos/11388016/pexels-photo-11388016.jpeg",
    "https://images.pexels.com/photos/1724193/pexels-photo-1724193.jpeg",
    "https://images.pexels.com/photos/22674331/pexels-photo-22674331.jpeg",
    "https://images.pexels.com/photos/302894/pexels-photo-302894.jpeg",
    "https://images.pexels.com/photos/3026808/pexels-photo-3026808.jpeg",
  ];

  return (
    <div data-testid="landing-page" className="min-h-screen text-[#F5F0EB]">
      <Header />

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImg})`, transform: "scale(1.05)" }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-24 md:pt-32 pb-24 grid lg:grid-cols-12 gap-4 lg:gap-8 items-center">
          <HeroSpot lang={lang} />
          <div className="lg:col-span-7 lg:order-1">
            <div className="overline animate-fade-in delay-1">{t("hero.tagline")}</div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-[5rem] xl:text-[6.25rem] leading-[0.95] mt-6 tracking-tight font-light animate-fade-up delay-2">
              {heroTitle}<br />
              <span className="italic text-gold-gradient">{heroAccent}</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#A8A39D] max-w-xl leading-relaxed animate-fade-up delay-3">
              {heroSub}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up delay-4">
              <Link data-testid="hero-cta-discover" to="/discover" className="btn-gold">
                {t("hero.cta1")} <ArrowRight size={16} />
              </Link>
              <Link data-testid="hero-cta-register" to="/register" className="btn-ghost">
                {t("hero.cta2")} <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 marquee text-[#A8A39D] text-xs tracking-[0.4em] uppercase">
          <div className="marquee-inner">
            <span className="px-12">Cafés</span>·
            <span className="px-12">Boulangeries</span>·
            <span className="px-12">Pâtisseries artisanales</span>·
            <span className="px-12">Réservation directe</span>·
            <span className="px-12">100 % Québec</span>·
            <span className="px-12">Cafés</span>·
            <span className="px-12">Boulangeries</span>·
            <span className="px-12">Pâtisseries artisanales</span>·
            <span className="px-12">Réservation directe</span>·
            <span className="px-12">100 % Québec</span>·
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="py-32 md:py-40 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline mb-6">{t("sections.experienceLabel")}</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light">
                {t("sections.experienceTitle")}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Reveal delay={0.1}>
              <div className="luxe-card p-8 h-full">
                <Coffee className="text-[#C5A059]" size={28} />
                <div className="font-serif text-2xl mt-6 mb-2">Découvrir</div>
                <p className="text-[#A8A39D] text-sm leading-relaxed">Parcourez les cafés, boulangeries et pâtisseries artisanales du Québec.</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="luxe-card p-8 h-full" style={{ background: 'linear-gradient(135deg, #1C1916, #141210)' }}>
                <Calendar className="text-[#C5A059]" size={28} />
                <div className="font-serif text-2xl mt-6 mb-2">Réserver</div>
                <p className="text-[#A8A39D] text-sm leading-relaxed">Réservez une table directement auprès du commerce. Simple et sans commission.</p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="luxe-card p-8 h-full sm:col-span-2">
                <MapPin className="text-[#C5A059]" size={28} />
                <div className="font-serif text-2xl mt-6 mb-2">Se rendre sur place</div>
                <p className="text-[#A8A39D] text-sm leading-relaxed max-w-2xl">Adresse, itinéraire, appel direct, horaires. CAFÉ-SPOT vous accompagne jusqu'à votre table.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-32 md:py-40 px-6 md:px-12 bg-[#0E0C0B] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <div className="overline mb-5">{t("sections.featuresLabel")}</div>
              <h2 className="font-serif text-4xl md:text-6xl font-light max-w-3xl mx-auto">
                {t("sections.featuresTitle")}
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {features.map((f, i) => {
              const Icon = featureIcons[i] || Coffee;
              return (
                <Reveal key={i} delay={0.05 * i}>
                  <div data-testid={`feature-card-${i}`} className="bg-[#0E0C0B] p-10 hover:bg-[#141210] transition-colors duration-500 group h-full">
                    <div className="flex items-center justify-between mb-8">
                      <div className="w-12 h-12 border border-[#C5A059]/30 flex items-center justify-center group-hover:border-[#C5A059] group-hover:bg-[#C5A059]/10 transition-all duration-500">
                        <Icon className="text-[#C5A059]" size={20} />
                      </div>
                      <span className="text-xs text-[#A8A39D] tracking-[0.3em]">0{i + 1}</span>
                    </div>
                    <div className="font-serif text-2xl mb-3">{f.t}</div>
                    <p className="text-[#A8A39D] text-sm leading-relaxed">{f.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CAFÉS — public discovery, no login needed */}
      <section data-testid="landing-cafes" className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
              <div className="flex items-end gap-5">
                <Spot variant="walk" testId="landing-cafes-spot" alt="" className="hidden sm:block w-20 md:w-24 h-auto -mb-1" />
              <div>
                <div className="overline mb-4">{lang === "en" ? "Discover" : "Découvrir"}</div>
                <h2 className="font-serif text-4xl md:text-6xl font-light max-w-2xl leading-[1.05]">
                  {lang === "en" ? "Our featured cafés" : "Nos commerces à découvrir"}
                </h2>
                <p className="text-[#A8A39D] text-sm mt-4 max-w-lg">
                  {lang === "en"
                    ? "Browse the artisan cafés, bakeries and pastry shops on CAFÉ-SPOT. No account required — just click a card to see the full page."
                    : "Explorez les cafés, boulangeries et pâtisseries artisanales sur CAFÉ-SPOT. Aucun compte requis — cliquez pour découvrir la fiche complète."}
                </p>
              </div>
              </div>
              <Link to="/discover" className="btn-ghost">
                {lang === "en" ? "View all" : "Voir tous les commerces"} <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>

          {cafes.length === 0 ? (
            <div data-testid="landing-cafes-empty" className="border border-white/10 py-20 text-center">
              <Spot variant="wink" testId="landing-cafes-empty-spot" alt="" className="mx-auto mb-6 w-28 h-auto" />
              <div className="font-serif text-2xl mb-2 text-[#F5F0EB]">
                {lang === "en" ? "First cafés coming soon" : "Les premiers commerces arrivent bientôt"}
              </div>
              <div className="text-[#A8A39D] text-sm max-w-md mx-auto">
                {lang === "en"
                  ? "The platform is just launching. Are you a café owner? Register to be one of the first."
                  : "La plateforme se lance. Vous êtes commerçant ? Inscrivez-vous pour être parmi les premiers."}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cafes.slice(0, 6).map((c, i) => (
                <Reveal key={c.id} delay={0.04 * i}>
                  <Link to={`/cafe/${c.slug}`} data-testid={`landing-cafe-${c.slug}`} className="luxe-card group block relative h-full">
                    {c.is_draft && (
                      <div className="absolute top-3 left-3 z-10 px-2 py-1 bg-[#0A0908]/80 text-[#A8A39D] text-[10px] tracking-widest uppercase border border-white/20">
                        {lang === "en" ? "Coming soon" : "Bientôt"}
                      </div>
                    )}
                    {c.sponsored && (
                      <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-[#C5A059] text-[#0A0908] text-[10px] tracking-widest uppercase flex items-center gap-1">
                        <Star size={10} /> Premium
                      </div>
                    )}
                    <div className="aspect-[4/3] overflow-hidden bg-[#141210]">
                      {c.banner_url ? (
                        <img src={c.banner_url} alt={c.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#5D4037] font-serif text-6xl">{c.name?.[0] || "?"}</div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="overline mb-2 text-xs">{c.category || "Commerce"}{c.city ? ` · ${c.city}` : ""}</div>
                      <div className="font-serif text-2xl leading-tight">{c.name}</div>
                      {c.description && <p className="text-[#A8A39D] text-sm mt-2 line-clamp-2">{c.description}</p>}
                      {(c.address || c.city) && (
                        <div className="mt-4 text-xs text-[#A8A39D] flex items-center gap-2"><MapPin size={12} className="text-[#C5A059]" /> {[c.city, c.province].filter(Boolean).join(", ")}</div>
                      )}
                      <div className="mt-4 text-xs tracking-[0.2em] uppercase text-[#C5A059] flex items-center gap-2 group-hover:gap-3 transition-all">
                        {lang === "en" ? "View" : "Voir la fiche"} <ArrowRight size={12} />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-32 md:py-40 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
              <div>
                <div className="overline mb-5">{t("sections.galleryLabel")}</div>
                <h2 className="font-serif text-4xl md:text-6xl font-light max-w-2xl">{t("sections.galleryTitle")}</h2>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {galleryImages.map((src, i) => (
              <Reveal key={i} delay={0.06 * i}>
                <div className={`overflow-hidden ${i === 0 ? "row-span-2 aspect-[4/5] md:aspect-auto md:h-full" : "aspect-square"}`}>
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-110 grayscale-[20%] hover:grayscale-0"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — direct sign-up entry point */}
      <section data-testid="landing-pricing" id="tarifs" className="py-32 md:py-40 px-6 md:px-12 bg-[#0E0C0B] border-y border-white/5">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <div className="overline mb-5">{lang === "en" ? "For businesses" : "Pour les commerces"}</div>
              <h2 className="font-serif text-4xl md:text-6xl font-light leading-tight">
                {lang === "en" ? "Two simple plans" : "Deux forfaits simples"}
              </h2>
              <p className="text-[#A8A39D] text-sm mt-5 max-w-xl mx-auto">
                {lang === "en"
                  ? "No commitment · cancel anytime · taxes extra (GST 5 % · QST 9.975 %)"
                  : "Sans engagement · annulable à tout moment · taxes en sus (TPS 5 % · TVQ 9,975 %)"}
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal delay={0.1}>
              <div data-testid="landing-plan-standard" className="luxe-card p-10 h-full flex flex-col">
                <div className="overline mb-4">Standard</div>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="font-serif text-6xl text-[#C5A059] leading-none">39</span>
                  <span className="text-[#A8A39D] text-sm">$ CAD / {lang === "en" ? "month" : "mois"}</span>
                </div>
                <ul className="space-y-3 mb-10 flex-1">
                  {[
                    lang === "en" ? "Logo and façade photo" : "Logo et photo de façade",
                    lang === "en" ? "Up to 15 gallery photos" : "Jusqu'à 15 photos dans la galerie",
                    lang === "en" ? "Menu, hours, contact info" : "Menu, horaires, coordonnées",
                    lang === "en" ? "Interactive map presence" : "Présence sur la carte interactive",
                    lang === "en" ? "Optional reservations" : "Réservations optionnelles",
                    lang === "en" ? "Basic stats" : "Statistiques de base",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#F5F0EB] leading-6">
                      <Check size={14} className="text-[#C5A059] flex-shrink-0 mt-1" strokeWidth={1.8} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  data-testid="landing-cta-standard"
                  to="/register?plan=standard&role=cafe_owner"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#0A0908] hover:bg-[#1C1916] text-[#F5F0EB] border-2 border-[#C5A059] hover:border-[#D4AF37] px-6 py-4 text-sm tracking-[0.25em] uppercase font-medium transition-all duration-200 hover:shadow-[0_0_30px_rgba(197,160,89,0.25)]"
                >
                  {lang === "en" ? "Get started · Standard" : "Commencer · Standard"}
                  <ArrowRight size={16} strokeWidth={2} />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div data-testid="landing-plan-premium" className="luxe-card p-10 h-full flex flex-col relative border-[#C5A059]/50">
                <div className="absolute top-0 right-6 -translate-y-1/2 bg-[#C5A059] text-[#0A0908] text-[10px] tracking-[0.3em] uppercase px-3 py-1 flex items-center gap-1.5">
                  <Star size={10} fill="currentColor" /> {lang === "en" ? "Recommended" : "Recommandé"}
                </div>
                <div className="overline mb-4">Premium</div>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="font-serif text-6xl text-[#C5A059] leading-none">59</span>
                  <span className="text-[#A8A39D] text-sm">$ CAD / {lang === "en" ? "month" : "mois"}</span>
                </div>
                <ul className="space-y-3 mb-10 flex-1">
                  {[
                    lang === "en" ? "Everything in Standard" : "Tout le Standard",
                    lang === "en" ? "Up to 30 gallery photos" : "Jusqu'à 30 photos dans la galerie",
                    lang === "en" ? "Premium badge" : "Badge Premium",
                    lang === "en" ? "Priority visibility" : "Visibilité prioritaire dans les résultats",
                    lang === "en" ? "Highlighted on map" : "Mise en avant sur la carte",
                    lang === "en" ? "Featured businesses section" : "Apparition dans les commerces vedettes",
                    lang === "en" ? "Advanced stats" : "Statistiques avancées",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#F5F0EB] leading-6">
                      <Check size={14} className="text-[#C5A059] flex-shrink-0 mt-1" strokeWidth={1.8} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  data-testid="landing-cta-premium"
                  to="/register?plan=premium&role=cafe_owner"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A0908] border-2 border-[#C5A059] hover:border-[#D4AF37] px-6 py-4 text-sm tracking-[0.25em] uppercase font-medium transition-all duration-200 hover:shadow-[0_0_30px_rgba(197,160,89,0.35)]"
                >
                  {lang === "en" ? "Get started · Premium" : "Commencer · Premium"}
                  <ArrowRight size={16} strokeWidth={2} />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.3}>
            <div className="text-center mt-10 text-xs text-[#A8A39D] tracking-wide">
              {lang === "en" ? (
                <>Already have an account? <Link to="/login" className="text-[#C5A059] hover:underline">Sign in</Link></>
              ) : (
                <>Déjà inscrit ? <Link to="/login" className="text-[#C5A059] hover:underline">Se connecter</Link></>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 md:py-40 px-6 md:px-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/4533eb4f-727d-4fa4-a61b-ad5c3b4e489f/images/8fa2dd5a9fdc33e1e3210f034f33db613ad3de2ce7057306f91d9d5b325a677c.png)", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908] via-transparent to-[#0A0908]" />
        <div className="max-w-[900px] mx-auto text-center relative">
          <Reveal>
            <div className="overline mb-6">Pour les commerces · {t("footer.region")}</div>
            <h2 className="font-serif text-4xl md:text-6xl font-light leading-[1.05]">{ctaTitle}</h2>
            <p className="text-[#A8A39D] mt-8 max-w-xl mx-auto">{ctaSub}</p>
            <div className="mt-12">
              <Link data-testid="cta-register" to="/register" className="btn-gold">
                {t("sections.ctaBtn")} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <SponsorsBanner />
      <Footer />
    </div>
  );
}
